import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { settingsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Store } from 'lucide-react';

export default function SettingsPage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailNotif, setEmailNotif] = useState(true);
  const [alertNotif, setAlertNotif] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await settingsAPI.getProfile();
        setProfile(res.data);
        setEmailNotif(res.data.notifications_email);
        setAlertNotif(res.data.notifications_alerts);
      } catch (err) {
        console.error('Failed to load profile:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateProfile({ notifications_email: emailNotif, notifications_alerts: alertNotif });
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your account preferences</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6" data-testid="settings-tabs">
        <TabsList className="bg-muted/30">
          <TabsTrigger value="profile" className="text-sm">Profile</TabsTrigger>
          <TabsTrigger value="notifications" className="text-sm">Notifications</TabsTrigger>
          <TabsTrigger value="connections" className="text-sm">Connections</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card className="bg-card border-border/80">
            <CardHeader>
              <CardTitle className="text-base font-semibold">Profile Information</CardTitle>
              <CardDescription className="text-xs">Your account details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Name</Label>
                  <Input value={profile?.name || ''} readOnly className="bg-background/40 border-border h-10" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Email</Label>
                  <Input value={profile?.email || ''} readOnly className="bg-background/40 border-border h-10" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Role</Label>
                  <Input value={profile?.role || ''} readOnly className="bg-background/40 border-border h-10" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card className="bg-card border-border/80">
            <CardHeader>
              <CardTitle className="text-base font-semibold">Notification Preferences</CardTitle>
              <CardDescription className="text-xs">Configure how you receive alerts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Email Notifications</p>
                  <p className="text-xs text-muted-foreground">Receive risk score updates via email</p>
                </div>
                <Switch checked={emailNotif} onCheckedChange={setEmailNotif} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Critical Alert Notifications</p>
                  <p className="text-xs text-muted-foreground">Get notified when metrics breach thresholds</p>
                </div>
                <Switch checked={alertNotif} onCheckedChange={setAlertNotif} />
              </div>
              <Button onClick={handleSave} disabled={saving} data-testid="settings-save-button" className="grad-accent text-white border-0 hover:opacity-90 transition-opacity">
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="connections">
          <Card className="bg-card border-border/80">
            <CardHeader>
              <CardTitle className="text-base font-semibold">Connected Stores</CardTitle>
              <CardDescription className="text-xs">Manage your Amazon store connections</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-8 border border-dashed border-border/80 rounded-lg text-center">
                <Store className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="text-sm font-medium mb-2">Connect Amazon Store</h3>
                <p className="text-xs text-muted-foreground mb-4 max-w-sm mx-auto">
                  SP-API integration coming soon. Your data will sync automatically once connected.
                </p>
                <Button variant="outline" className="border-border/60 text-muted-foreground" disabled>
                  Connect Amazon Store
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
